import axios, { type AxiosInstance } from 'axios';
import type { IEnvelope, IOrgTokenResponse, ISessionResponse } from '@stem/contracts';

const USER_TOKEN_KEY = 'stem.token.user';
const ORG_TOKEN_KEY = 'stem.token.org';
const ORG_SLUG_KEY = 'stem.org.slug';

/**
 * The one way the SPA reaches data.
 *
 * There is no Supabase client for tables anywhere in this app — no PostgREST,
 * no `supabase.from('courses')`. The browser holds a GoTrue session and an org
 * token, and everything else goes through NestJS. That is what makes the
 * permission catalog and the entitlement gate mean something: a second door
 * into the same rows would route around both.
 *
 * The class holds the two tokens and unwraps the `{ status, data, meta }`
 * envelope, so no caller ever writes `response.data.data`.
 */
class ApiClient {
  private readonly http: AxiosInstance;
  private userToken: string | null = null;
  private orgToken: string | null = null;
  private orgSlug: string | null = null;
  private onUnauthorized: (() => void) | null = null;

  constructor(baseURL: string) {
    this.http = axios.create({ baseURL, timeout: 30_000 });

    if (typeof localStorage !== 'undefined') {
      this.userToken = localStorage.getItem(USER_TOKEN_KEY);
      this.orgToken = localStorage.getItem(ORG_TOKEN_KEY);
      this.orgSlug = localStorage.getItem(ORG_SLUG_KEY);
    }

    this.http.interceptors.response.use(
      response => response,
      error => {
        // A 401 means the org token expired — eight hours, so it happens to
        // anyone who leaves a tab open overnight. The session is cleared and
        // the app sends them back to sign in rather than showing an empty
        // dashboard that looks like data loss.
        if (error?.response?.status === 401) this.onUnauthorized?.();
        return Promise.reject(normalizeError(error));
      },
    );
  }

  setUnauthorizedHandler(handler: () => void): void {
    this.onUnauthorized = handler;
  }

  // ---------------------------------------------------------------- tokens

  setUserToken(token: string | null): void {
    this.userToken = token;
    if (typeof localStorage === 'undefined') return;
    if (token) localStorage.setItem(USER_TOKEN_KEY, token);
    else localStorage.removeItem(USER_TOKEN_KEY);
  }

  getUserToken(): string | null {
    return this.userToken;
  }

  getOrgSlug(): string | null {
    return this.orgSlug;
  }

  hasOrgToken(): boolean {
    return Boolean(this.orgToken);
  }

  clear(): void {
    this.setUserToken(null);
    this.orgToken = null;
    this.orgSlug = null;
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(ORG_TOKEN_KEY);
    localStorage.removeItem(ORG_SLUG_KEY);
  }

  // ----------------------------------------------------------------- calls

  /** Identity-scoped: `/auth/*` and `/v1/join/*`, before an org is chosen. */
  async user<T>(method: 'get' | 'post' | 'patch', path: string, body?: unknown): Promise<T> {
    const response = await this.http.request<IEnvelope<T>>({
      method,
      url: path,
      data: body,
      headers: { authorization: `Bearer ${this.userToken ?? ''}` },
    });
    return unwrap(response.data);
  }

  /** Org-scoped: everything under `/v1/*`. */
  async request<T>(
    method: 'get' | 'post' | 'put' | 'patch' | 'delete',
    path: string,
    body?: unknown,
  ): Promise<T> {
    const response = await this.http.request<IEnvelope<T>>({
      method,
      url: path,
      data: body,
      headers: { authorization: `Bearer ${this.orgToken ?? ''}` },
    });
    return unwrap(response.data);
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>('get', path);
  }
  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('post', path, body);
  }
  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('put', path, body);
  }
  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('patch', path, body);
  }
  delete<T>(path: string): Promise<T> {
    return this.request<T>('delete', path);
  }

  /** Public: `/health`, `/verify/:serial`. */
  async open<T>(path: string): Promise<T> {
    const response = await this.http.get<IEnvelope<T>>(path);
    return unwrap(response.data);
  }

  // --------------------------------------------------------------- session

  async session(persona?: string): Promise<ISessionResponse> {
    return this.user<ISessionResponse>('post', '/auth/session', persona ? { persona } : {});
  }

  /** Step 4 of the two-token flow. Also how switching schools works. */
  async exchangeOrgToken(slug: string): Promise<IOrgTokenResponse> {
    const result = await this.user<IOrgTokenResponse>('post', `/auth/org/${slug}/token`);
    this.orgToken = result.accessToken;
    this.orgSlug = slug;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(ORG_TOKEN_KEY, result.accessToken);
      localStorage.setItem(ORG_SLUG_KEY, slug);
    }
    return result;
  }
}

function unwrap<T>(envelope: IEnvelope<T>): T {
  if (envelope.status === 'success') return envelope.data;
  throw new ApiError(envelope.message, 400, envelope.errors);
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly errors: Array<{ property: string; constraints: string[] }> = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /**
   * 402 is the one status the UI must branch on.
   *
   * 403 means "ask your administrator"; 402 means "here is the upgrade page".
   * Sending a teacher who needs to buy a subscription to their head teacher
   * instead is a lost sale and a confused user, so the distinction earns a
   * helper rather than a magic number at four call sites.
   */
  get needsUpgrade(): boolean {
    return this.status === 402;
  }
}

function normalizeError(error: unknown): ApiError {
  const response = (error as { response?: { status: number; data?: any } })?.response;
  if (response) {
    return new ApiError(
      response.data?.message ?? `Request failed (${response.status})`,
      response.status,
      response.data?.errors ?? [],
    );
  }
  // No response at all: offline, DNS, or the API is down. Distinguishing this
  // matters because the offline queue treats it as "try again later" rather
  // than as a rejection.
  return new ApiError('Cannot reach the server', 0);
}

export const apiClient = new ApiClient(import.meta.env.VITE_API_URL ?? '/api');
